import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { projectCategories, projects } from "../data/content";
import { ArrowUpRight } from "./Icons";
import { SectionHeader } from "./SectionHeader";
import "./Projects.css";

function ProjectCard({ project, featured = false }) {
  // Projetos com um destino só têm o card inteiro clicável (link em overlay);
  // com vários (lojas de app, por exemplo), cada link vira um alvo próprio.
  const links =
    project.links ??
    (project.link ? [{ label: "Ver projeto", href: project.link }] : []);
  const single = links.length === 1;

  return (
    <article
      className={`project ${featured ? "project--featured" : ""} ${
        single ? "project--single" : ""
      }`}
    >
      <div
        className={`project__media ${
          project.imageFit === "contain" ? "project__media--contain" : ""
        }`}
        style={
          project.imageBackground ? { background: project.imageBackground } : undefined
        }
      >
        <img src={project.image} alt="" loading="lazy" />
      </div>

      <div className="project__body">
        <div className="project__meta">
          <span className="project__category">
            {project.categories.join(" · ")}
          </span>
          {project.year && <span className="project__year">{project.year}</span>}
        </div>

        <h3 className="project__title">{project.title}</h3>

        {project.metrics && (
          <ul className="project__metrics">
            {project.metrics.map((metric) => (
              <li key={metric} className="project__metric">
                {metric}
              </li>
            ))}
          </ul>
        )}

        <p className="project__desc">{project.description}</p>

        <ul className="tag-row project__stack">
          {project.stack.map((item) => (
            <li key={item} className="tag">
              {item}
            </li>
          ))}
        </ul>

        {links.length > 0 && (
          <div className="project__links">
            {links.map((link) => (
              <a
                key={link.href}
                className={`project__cta ${single ? "project__cta--cover" : ""}`}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={
                  single
                    ? `Abrir projeto ${project.title}`
                    : `${project.title} — ${link.label}`
                }
              >
                {link.label}
                <ArrowUpRight size={16} className="icon icon--arrow" />
              </a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export function Projects() {
  const [filter, setFilter] = useState("Todos");
  const reduceMotion = useReducedMotion();

  const filtered = useMemo(
    () =>
      filter === "Todos"
        ? projects
        : projects.filter((project) => project.categories.includes(filter)),
    [filter]
  );

  const showFeatured = filter === "Todos" && filtered[0]?.featured;
  const featured = showFeatured ? filtered[0] : null;
  const rest = showFeatured ? filtered.slice(1) : filtered;

  return (
    <section id="projetos" className="section container">
      <SectionHeader
        eyebrow="Portfólio"
        title="Projetos selecionados"
        description="Sistemas, dashboards e automações que construí do zero — do primeiro commit ao deploy."
      />

      <div className="projects__filters" role="tablist" aria-label="Filtrar projetos">
        {projectCategories.map((category) => {
          const count =
            category === "Todos"
              ? projects.length
              : projects.filter((p) => p.categories.includes(category)).length;

          return (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={filter === category}
              className={`filter ${filter === category ? "is-active" : ""}`}
              onClick={() => setFilter(category)}
            >
              {category}
              <span className="filter__count">{count}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={filter}
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
        >
          {featured && (
            <div className="projects__featured">
              <ProjectCard project={featured} featured />
            </div>
          )}

          <div className="projects__grid">
            {rest.map((project) => (
              <ProjectCard key={project.title} project={project} />
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="projects__empty muted">
              Nenhum projeto nesta categoria por enquanto.
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
